import React from 'react';

const PartA3 = ({ data, pageNumber = 1 }) => {
    // Handle both page 1 and page 2 of Part A3
    const isPage1 = pageNumber === 1;

    if (isPage1) {
        return <PartA3Page1 data={data} />;
    } else {
        return <PartA3Page2 data={data} />;
    }
};

// Page 1: Time Management Statements
const PartA3Page1 = ({ data }) => {
    const statements = [
        { key: 'follow_schedule', text: 'I like following a schedule.' },
        { key: 'get_distracted', text: 'I get distracted and do not start my tasks on time.' },
        { key: 'last_minute', text: 'I do things at the last minute.' },
        { key: 'figure_time', text: 'I can figure out how much time assignments and projects will take.' },
        { key: 'decide_tasks', text: 'I am good at deciding which tasks to do first.' }
    ];

    const options = ['Rarely', 'Sometimes', 'Usually', 'Almost always'];

    return (
        <div className="report-page relative font-[poppins] bg-white" style={{
            width: '210mm',
            height: '297mm',
            padding: '20mm',
            margin: '0 auto',
            fontSize: '10pt',
            lineHeight: '1.4',
            color: '#333333',
            boxSizing: 'border-box',
            overflow: 'hidden',
            position: 'relative',
            breakInside: 'avoid',
            pageBreakInside: 'avoid'
        }}>
            {/* Orange corner triangles */}
            <div className="absolute top-0 right-0 w-0 h-0"
                style={{
                    borderTop: '80px solid #f1905b',
                    borderLeft: '80px solid transparent'
                }}>
            </div>
            <div className="absolute bottom-0 left-0 w-0 h-0"
                style={{
                    borderBottom: '80px solid #F97316',
                    borderRight: '80px solid transparent'
                }}>
            </div>

            {/* Header */}
            <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-[#F97316] mb-1">PART A (3)</h2>
                <h3 className="text-lg font-bold text-black">Time Management</h3>
            </div>

            <div className="font-bold text-sm mb-2">Tick (✓) the most appropriate option.</div>

            {/* Statements Table */}
            <div className="mb-6 border border-[#F97316]/50">
                <div className="grid grid-cols-5 bg-[#FFEDD5] border-b border-[#F97316]/50">
                    <div className="col-span-1 p-2 font-bold text-center border-r border-[#F97316]/50 flex items-center justify-center text-xs">Statements</div>
                    {options.map((opt) => (
                        <div key={opt} className="p-2 font-bold text-center border-r border-[#F97316]/50 last:border-r-0 text-xs flex items-center justify-center">
                            {opt}
                        </div>
                    ))}
                </div>

                {statements.map((statement, index) => (
                    <div key={statement.key} className="grid grid-cols-5 border-b border-[#F97316]/50 last:border-b-0">
                        <div className="col-span-1 p-2 border-r border-[#F97316]/50 text-xs font-medium flex items-start">
                            <span className="mr-1 font-bold">{index + 1}.</span>
                            {statement.text}
                        </div>
                        {options.map((opt) => (
                            <div key={opt} className="p-2 border-r border-[#F97316]/50 last:border-r-0 flex items-center justify-center">
                                {data?.[statement.key]?.toLowerCase() === opt.toLowerCase().replace(' ', '_') && (
                                    <span className="text-lg font-bold text-black">✓</span>
                                )}
                            </div>
                        ))}
                    </div>
                ))}
            </div>

            {/* My Time Habits Section */}
            <div className="border border-blue-200">
                <div className="bg-[#BFDBFE] p-2 text-center font-bold text-lg text-black flex items-center justify-center gap-2">
                    <span>📅</span>
                    <span>My Time Habits</span>
                </div>

                <div className="grid grid-cols-4 divide-x divide-blue-200 bg-[#EFF6FF]">
                    {/* Column 1 */}
                    <div className="p-2 flex flex-col gap-4">
                        <div className="font-medium text-xs text-center bg-blue-100 p-1 mb-1">What time of the day is best for you...</div>

                        <div className="text-center">
                            <div className="text-[10px] font-semibold mb-0.5">...to study?</div>
                            <div className="text-[10px] min-h-[1.5em] border-b border-blue-200">{data?.best_time_study}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-[10px] font-semibold mb-0.5">...to play/exercise?</div>
                            <div className="text-[10px] min-h-[1.5em] border-b border-blue-200">{data?.best_time_play}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-[10px] font-semibold mb-0.5">...to relax?</div>
                            <div className="text-[10px] min-h-[1.5em] border-b border-blue-200">{data?.best_time_relax}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-[10px] font-semibold mb-0.5">...to spend time with friends and family?</div>
                            <div className="text-[10px] min-h-[1.5em] border-b border-blue-200">{data?.best_time_friends}</div>
                        </div>
                    </div>

                    {/* Column 2 */}
                    <div className="p-2 flex flex-col justify-center">
                        <div className="font-medium text-xs text-center bg-blue-100 p-1 mb-2">How much time do you spend on schoolwork after school?</div>
                        <div className="text-xs text-center min-h-[4em] flex items-center justify-center">{data?.time_schoolwork}</div>
                    </div>

                    {/* Column 3 */}
                    <div className="p-2 flex flex-col gap-4">
                        <div>
                            <div className="font-medium text-xs text-center bg-blue-100 p-1 mb-1">How much time do you spend on physical fitness?</div>
                            <div className="text-xs text-center min-h-[2em] flex items-center justify-center">{data?.time_fitness}</div>
                        </div>
                        <div>
                            <div className="font-medium text-xs text-center bg-blue-100 p-1 mb-1">How much time do you spend taking care of your emotional wellbeing?</div>
                            <div className="text-xs text-center min-h-[2em] flex items-center justify-center">{data?.time_emotional_wellbeing}</div>
                        </div>
                        <div>
                            <div className="font-medium text-xs text-center bg-blue-100 p-1 mb-1">What do you do to improve your physical and mental fitness?</div>
                            <div className="text-xs text-center min-h-[2em] flex items-center justify-center">{data?.improve_fitness}</div>
                        </div>
                    </div>

                    {/* Column 4 */}
                    <div className="p-2 flex flex-col gap-4">
                        <div>
                            <div className="font-medium text-xs text-center bg-blue-100 p-1 mb-1">How much time do you spend on other things? (With friends, on your phone, etc.)</div>
                            <div className="text-xs text-center min-h-[2em] flex items-center justify-center">{data?.time_other_things}</div>
                        </div>
                        <div className="mt-auto">
                            <div className="font-medium text-xs text-center bg-blue-100 p-1 mb-1">Do you make to-do lists for your daily tasks? Why / Why not?</div>
                            <div className="text-xs text-center min-h-[3em] flex items-center justify-center">{data?.make_todo_lists}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Page Number */}
            <div className="absolute bottom-3 left-0 right-0 text-center text-xs font-medium">
                4
            </div>
        </div>
    );
};

// Page 2: Time Preferences and Time Map
const PartA3Page2 = ({ data }) => {
    return (
        <div className="report-page relative font-[poppins] bg-white" style={{
            width: '210mm',
            height: '297mm',
            padding: '20mm',
            margin: '0 auto',
            fontSize: '10pt',
            lineHeight: '1.4',
            color: '#333333',
            boxSizing: 'border-box',
            overflow: 'hidden',
            position: 'relative',
            breakInside: 'avoid',
            pageBreakInside: 'avoid'
        }}>
            {/* Orange corner triangles */}
            <div className="absolute top-0 right-0 w-0 h-0"
                style={{
                    borderTop: '80px solid #f1905b',
                    borderLeft: '80px solid transparent'
                }}>
            </div>
            <div className="absolute bottom-0 left-0 w-0 h-0"
                style={{
                    borderBottom: '80px solid #F97316',
                    borderRight: '80px solid transparent'
                }}>
            </div>

            {/* Preferences Section */}
            <div className="grid grid-cols-2 gap-6 mb-8 mt-4">
                <div className="bg-[#DBEAFE] p-4 rounded-xl flex flex-col h-32">
                    <div className="font-bold text-sm mb-2 text-center">
                        One thing I would like to spend <span className="text-[#F97316]">more time</span> on:
                    </div>
                    <div className="flex-1 flex items-center justify-center text-center font-medium text-sm">
                        {data?.spend_more_time_on}
                    </div>
                </div>

                <div className="bg-[#DBEAFE] p-4 rounded-xl flex flex-col h-32">
                    <div className="font-bold text-sm mb-2 text-center">
                        One thing I would like to spend <span className="text-[#F97316]">less time</span> on:
                    </div>
                    <div className="flex-1 flex items-center justify-center text-center font-medium text-sm">
                        {data?.spend_less_time_on}
                    </div>
                </div>
            </div>

            {/* Time Map Header */}
            <div className="text-center mb-4">
                <h2 className="text-xl font-bold text-[#F97316] mb-1">Time Map</h2>
                <p className="font-bold text-xs mb-1">How do you plan your day? Use the clocks to map the activities you do in a day. Later, think about the following questions:</p>
                <div className="text-xs text-gray-600 mb-2">
                    <ul className="list-disc list-inside inline-block text-left">
                        <li>Is there any activity you should be spending <strong>more time</strong> on?</li>
                        <li>Is there any activity you should be spending <strong>less time</strong> on?</li>
                    </ul>
                </div>
                <p className="text-xs italic text-gray-500">As a follow-up, ask any peer to comment on your schedule and answer the questions above.</p>
            </div>

            {/* Clocks Section */}
            <div className="flex justify-center gap-12 mb-8 relative">
                {/* AM Section */}
                <div className="relative">
                    <div className="absolute -left-8 top-0 bottom-0 w-10 bg-[#FEF3C7] flex items-center justify-center rounded-l -z-10">
                        <span className="font-bold text-lg text-[#F97316] -rotate-90">AM</span>
                    </div>
                    <div className="w-48 h-48 border-2 border-black rounded-full relative bg-white">
                        {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(num => (
                            <div
                                key={num}
                                className="absolute font-bold text-sm"
                                style={{
                                    top: num === 12 ? '5px' :
                                        num === 6 ? 'calc(100% - 20px)' :
                                            num === 3 ? '50%' :
                                                num === 9 ? '50%' :
                                                    `${50 + 40 * Math.cos((num - 3) * Math.PI / 6)}%`,
                                    left: num === 12 ? '50%' :
                                        num === 6 ? '50%' :
                                            num === 3 ? 'calc(100% - 15px)' :
                                                num === 9 ? '5px' :
                                                    `${50 + 40 * Math.sin((num - 3) * Math.PI / 6)}%`,
                                    transform: 'translate(-50%, -50%)'
                                }}
                            >
                                {num}
                            </div>
                        ))}
                        {/* Ticks */}
                        {[...Array(60)].map((_, i) => (
                            <div
                                key={i}
                                className={`absolute bg-black origin-bottom left-1/2 top-1/2 ${i % 5 === 0 ? 'h-2 w-0.5' : 'h-1 w-px'}`}
                                style={{
                                    transform: `translate(-50%, -100%) rotate(${i * 6}deg) translateY(-88px)`
                                }}
                            />
                        ))}
                        <div className="absolute top-1/2 left-1/2 w-1.5 h-1.5 bg-black rounded-full transform -translate-x-1/2 -translate-y-1/2 z-10"></div>
                    </div>
                </div>

                {/* PM Section */}
                <div className="relative">
                    <div className="absolute -left-8 top-0 bottom-0 w-10 bg-[#E5E7EB] flex items-center justify-center rounded-l -z-10">
                        <span className="font-bold text-lg text-gray-600 -rotate-90">PM</span>
                    </div>
                    <div className="w-48 h-48 border-2 border-black rounded-full relative bg-white">
                        {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(num => (
                            <div
                                key={num}
                                className="absolute font-bold text-sm"
                                style={{
                                    top: num === 12 ? '5px' :
                                        num === 6 ? 'calc(100% - 20px)' :
                                            num === 3 ? '50%' :
                                                num === 9 ? '50%' :
                                                    `${50 + 40 * Math.cos((num - 3) * Math.PI / 6)}%`,
                                    left: num === 12 ? '50%' :
                                        num === 6 ? '50%' :
                                            num === 3 ? 'calc(100% - 15px)' :
                                                num === 9 ? '5px' :
                                                    `${50 + 40 * Math.sin((num - 3) * Math.PI / 6)}%`,
                                    transform: 'translate(-50%, -50%)'
                                }}
                            >
                                {num}
                            </div>
                        ))}
                        {/* Ticks */}
                        {[...Array(60)].map((_, i) => (
                            <div
                                key={i}
                                className={`absolute bg-black origin-bottom left-1/2 top-1/2 ${i % 5 === 0 ? 'h-2 w-0.5' : 'h-1 w-px'}`}
                                style={{
                                    transform: `translate(-50%, -100%) rotate(${i * 6}deg) translateY(-88px)`
                                }}
                            />
                        ))}
                        <div className="absolute top-1/2 left-1/2 w-1.5 h-1.5 bg-black rounded-full transform -translate-x-1/2 -translate-y-1/2 z-10"></div>
                    </div>
                </div>
            </div>

            {/* Follow-up Questions */}
            <div className="mt-auto mb-6">
                <div className="space-y-2 text-xs">
                    <div>
                        <span className="font-bold">Activity I should spend MORE time on: </span>
                        <span className="bg-gray-50 p-1 border-b border-gray-400 inline-block min-w-[200px]">
                            {data?.activity_more_time}
                        </span>
                    </div>
                    <div>
                        <span className="font-bold">Activity I should spend LESS time on: </span>
                        <span className="bg-gray-50 p-1 border-b border-gray-400 inline-block min-w-[200px]">
                            {data?.activity_less_time}
                        </span>
                    </div>
                    <div>
                        <span className="font-bold">Peer feedback on my schedule: </span>
                        <span className="bg-gray-50 p-1 border-b border-gray-400 inline-block min-w-[200px]">
                            {data?.peer_feedback}
                        </span>
                    </div>
                </div>
            </div>

            {/* Page Number */}
            <div className="absolute bottom-3 left-0 right-0 text-center text-xs font-medium">
                5
            </div>
        </div>
    );
};

export default PartA3;